import { useEffect, useMemo, useState } from 'react';
import { PhoneField, SelectField, TextArea, TextField } from '../components/FormField';
import Section from '../components/Section';
import { scrollToFirstError } from '../utils/scrollToFirstError';
import {
  DEFAULT_INTRO,
  DEFAULT_PROPOSAL_TITLE,
  DEFAULT_SUPPORT_BULLETS,
} from '../services/agreements';

// Payment-term presets offered in the dropdown. "Other" is the escape
// hatch — picking it reveals a free-text input so Directors can enter
// non-standard terms like "Net 45" or "Due on receipt" without the
// dropdown getting cluttered with every possible variant.
const PAYMENT_TERMS_OPTIONS = ['Net 30', 'Net 60', 'Net 90', 'Other'];

// Hours units offered on each coverage row. Mirrors the supplier model's
// units so the value we stamp onto the supplier's plant assignment reads
// consistently. "Per Production Day" is first because it's the most common
// shape for a liaison-coverage proposal.
const HOURS_UNIT_OPTIONS = [
  'Per Production Day',
  'Per Shift',
  'Per Day',
  'Per Week',
  'Per Month',
];

// Sentinel value for the supplier picker meaning "create a brand-new
// customer when this proposal is sent." Anything else is an existing
// supplier's code.
const NEW_SUPPLIER = 'NEW';

// Map a stored PaymentTerms string back to the dropdown choice. Used on
// initial load and whenever `initial` swaps — "Net 60" → "Net 60";
// anything else non-empty (e.g. "Net 45") → "Other".
function derivePaymentTermsChoice(stored) {
  const s = (stored || '').trim();
  if (!s) return 'Net 60';
  if (PAYMENT_TERMS_OPTIONS.includes(s) && s !== 'Other') return s;
  return 'Other';
}

// Work out the supplier picker's initial selection from a loaded agreement:
//   - linked agreement (has SupplierCode) → that code
//   - legacy/unlinked agreement that already names a company → "New" so the
//     typed company is preserved (the Director can switch to an existing
//     supplier if they'd rather link it)
//   - brand-new proposal → '' (forces a choice)
function deriveSupplierChoice(initial) {
  if (initial?.SupplierCode) return String(initial.SupplierCode);
  if (initial?.CustomerCompany) return NEW_SUPPLIER;
  return '';
}

// One empty coverage row. `plant` is the display label used on the printed
// PDF; `plantCode` is the real code used to attach the proposal to the
// supplier's plant assignment. `coverage` is the PDF text, auto-composed from
// hoursAllocated + hoursUnit on save.
function emptyCoverageRow() {
  return {
    plant: '',
    plantCode: '',
    hourlyRateUsd: '',
    hoursAllocated: '',
    hoursUnit: '',
    coverage: '',
  };
}

function buildModel(initial) {
  const todayIso = new Date().toISOString().slice(0, 10);
  return {
    ProposalDate: todayIso,
    ProposalTitle: DEFAULT_PROPOSAL_TITLE,

    // Link to the supplier this proposal is for. Empty until the Director
    // picks an existing supplier or chooses "New customer". Persisted as a
    // plain column (auto-dropped if the SharePoint column isn't provisioned).
    SupplierCode: '',

    // Customer (Prepared For)
    CustomerContactName: '',
    CustomerCompany: '',
    CustomerStreet: '',
    CustomerCity: '',
    CustomerState: '',
    CustomerZip: '',
    CustomerPhone: '',

    // Proxemics rep (Prepared By)
    PreparedByName: '',
    PreparedByPhone: '',
    PreparedByEmail: '',

    // Body
    Intro: DEFAULT_INTRO,
    supportBullets: [...DEFAULT_SUPPORT_BULLETS],
    coverageRows: [emptyCoverageRow()],

    // Terms — defaults match the Proxemics proposal template.
    PaymentTerms: 'Net 60',
    CancellationNoticeDays: 30,
    AdditionalTerms: '',

    ...(initial || {}),
  };
}

/**
 * Customer Agreement form. Mirrors the structure of the Proxemics
 * "Proposal for Liaison Coverage" template so the on-screen form and
 * the generated PDF share a 1:1 field mapping.
 *
 * Fields are grouped into sections:
 *   1. Proposal (date, title)
 *   2. Prepared For (which supplier + customer info)
 *   3. Prepared By (Proxemics rep info)
 *   4. Content (intro, support bullets, coverage table)
 *   5. Terms & Conditions
 *
 * `suppliers` and `plants` power the supplier picker and the per-row plant
 * dropdown respectively. Both are optional — if omitted the form still works,
 * just without the convenience dropdowns.
 */
export default function AgreementForm({
  initial,
  suppliers = [],
  plants = [],
  saving = false,
  deleting = false,
  onSave,
  onCancel,
  onDelete,
}) {
  // Presence of initial.id means we're editing a saved agreement (as
  // opposed to creating a new one) — the "Update" / "Delete" buttons
  // and the different primary-action label key off this.
  const isEditing = !!initial?.id;
  const [model, setModel] = useState(() => buildModel(initial));
  const [errors, setErrors] = useState({});

  // Payment terms use a "dropdown + optional Other" pattern (same shape
  // we used for Vest Color on the request form). We track the dropdown
  // choice independently of the stored PaymentTerms string so switching
  // to "Other" can show the fill-in even while PaymentTerms is still
  // blank (otherwise a blank stored value would derive back to the
  // default and the fill-in would never appear).
  const [paymentTermsChoice, setPaymentTermsChoice] = useState(() =>
    derivePaymentTermsChoice(initial?.PaymentTerms),
  );

  // Supplier picker selection (separate from model.SupplierCode so the
  // "New customer" sentinel can be shown without polluting the saved code).
  const [supplierChoice, setSupplierChoice] = useState(() =>
    deriveSupplierChoice(initial),
  );

  useEffect(() => {
    setModel(buildModel(initial));
    setErrors({});
    setPaymentTermsChoice(derivePaymentTermsChoice(initial?.PaymentTerms));
    setSupplierChoice(deriveSupplierChoice(initial));
  }, [initial?.id]);

  const set = (k, v) => setModel((m) => ({ ...m, [k]: v }));

  // Supplier dropdown options: every supplier (code + name) plus the
  // "New customer" sentinel at the top. Sorted by name for scanability.
  const supplierOptions = useMemo(() => {
    const opts = (suppliers || [])
      .filter((s) => s.SupplierCode)
      .map((s) => ({
        value: String(s.SupplierCode),
        label: s.Title ? `${s.Title} (${s.SupplierCode})` : String(s.SupplierCode),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: NEW_SUPPLIER, label: '+ New customer (created on send)' }, ...opts];
  }, [suppliers]);

  // Picking an existing supplier prefills the Prepared-For block from the
  // supplier record so the Director doesn't retype the company + address.
  // Picking "New customer" just clears the link and leaves the fields for
  // manual entry (keeping anything already typed).
  const onSupplierChoice = (choice) => {
    setSupplierChoice(choice);
    if (choice === '' || choice === NEW_SUPPLIER) {
      set('SupplierCode', '');
      return;
    }
    const s = (suppliers || []).find(
      (x) => String(x.SupplierCode) === String(choice),
    );
    if (!s) {
      set('SupplierCode', choice);
      return;
    }
    const contact = (Array.isArray(s.contacts) && s.contacts[0]) || {};
    setModel((m) => ({
      ...m,
      SupplierCode: String(s.SupplierCode),
      CustomerCompany: s.Title || m.CustomerCompany,
      CustomerStreet: s.AddressStreet || m.CustomerStreet,
      CustomerCity: s.City || m.CustomerCity,
      CustomerState: s.State || m.CustomerState,
      CustomerZip: s.PostalCode || m.CustomerZip,
      CustomerContactName: contact.name || m.CustomerContactName,
      CustomerPhone: contact.phone || m.CustomerPhone,
    }));
    // Clear any prior supplier error now that a real choice is made.
    setErrors((prev) => {
      if (!prev.SupplierCode) return prev;
      const { SupplierCode: _drop, ...rest } = prev;
      return rest;
    });
  };

  const onPaymentTermsChoice = (choice) => {
    setPaymentTermsChoice(choice);
    if (choice === 'Other') {
      // Switching to Other clears the preset so the fill-in starts
      // empty. If they already had a custom value (e.g. "Net 45") from
      // a loaded agreement, leave it alone so the fill-in stays
      // pre-populated.
      if (PAYMENT_TERMS_OPTIONS.includes(model.PaymentTerms)) {
        set('PaymentTerms', '');
      }
    } else {
      set('PaymentTerms', choice);
    }
  };

  // ---- Support bullet repeater ----
  const setBullet = (i, value) =>
    setModel((m) => {
      const next = [...(m.supportBullets || [])];
      next[i] = value;
      return { ...m, supportBullets: next };
    });
  const addBullet = () =>
    setModel((m) => ({
      ...m,
      supportBullets: [...(m.supportBullets || []), ''],
    }));
  const removeBullet = (i) =>
    setModel((m) => ({
      ...m,
      supportBullets: (m.supportBullets || []).filter((_, idx) => idx !== i),
    }));

  // ---- Coverage row repeater ----
  const setRow = (i, patch) =>
    setModel((m) => {
      const next = [...(m.coverageRows || [])];
      next[i] = { ...next[i], ...patch };
      return { ...m, coverageRows: next };
    });
  const addRow = () =>
    setModel((m) => ({
      ...m,
      coverageRows: [...(m.coverageRows || []), emptyCoverageRow()],
    }));
  const removeRow = (i) =>
    setModel((m) => ({
      ...m,
      coverageRows: (m.coverageRows || []).filter((_, idx) => idx !== i),
    }));

  // Plant dropdown options for the coverage rows — every known plant as
  // { value: CODE, label: "Title (CODE)" }, sorted by label.
  const plantOptions = useMemo(
    () =>
      (plants || [])
        .filter((p) => p.PlantCode)
        .map((p) => ({
          value: String(p.PlantCode).toUpperCase(),
          label: p.Title
            ? `${p.Title} (${p.PlantCode})`
            : String(p.PlantCode),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [plants],
  );

  // Picking a plant stamps both the code (for the supplier link) and a
  // human label (for the PDF). Falls back to the raw code as the label if
  // the plant isn't in the list.
  const onRowPlant = (i, code) => {
    const p = (plants || []).find(
      (pl) => String(pl.PlantCode).toUpperCase() === String(code).toUpperCase(),
    );
    const label = p
      ? p.Title
        ? `${p.Title} (${p.PlantCode})`
        : String(p.PlantCode)
      : code;
    setRow(i, { plantCode: code, plant: label });
  };

  // Every field on this form is required except AdditionalTerms. Each
  // miss flags an individual error message on the offending field; the
  // two repeaters (support bullets, coverage rows) surface their errors
  // at the section level.
  const validate = () => {
    const e = {};
    const req = (k, msg) => {
      const v = model[k];
      if (v == null || (typeof v === 'string' && !v.trim())) {
        e[k] = msg;
      }
    };
    // Supplier link — must pick an existing supplier or "New customer".
    if (!supplierChoice) {
      e.SupplierCode = 'Pick a customer or choose "New customer".';
    }
    // Proposal
    if (!model.ProposalDate) {
      e.ProposalDate = 'Proposal date is required.';
    }
    req('ProposalTitle', 'Proposal title is required.');
    // Prepared For
    req('CustomerContactName', 'Customer contact name is required.');
    req('CustomerCompany', 'Customer company is required.');
    req('CustomerStreet', 'Street is required.');
    req('CustomerCity', 'City is required.');
    req('CustomerState', 'State / Province is required.');
    req('CustomerZip', 'Zip / Postal Code is required.');
    req('CustomerPhone', 'Customer phone is required.');
    // Prepared By
    req('PreparedByName', 'Prepared by name is required.');
    req('PreparedByPhone', 'Prepared by phone is required.');
    req('PreparedByEmail', 'Prepared by email is required.');
    // Content
    req('Intro', 'Intro paragraph is required.');
    const hasBullet = (model.supportBullets || []).some(
      (b) => (b || '').trim(),
    );
    if (!hasBullet) {
      e.supportBullets = 'Add at least one support bullet.';
    }
    // Coverage — at least one row with plant, rate, hours, and unit.
    const completeRow = (model.coverageRows || []).some(
      (r) =>
        (r.plantCode || '').toString().trim() &&
        (r.hourlyRateUsd || '').toString().trim() &&
        (r.hoursAllocated || '').toString().trim() &&
        (r.hoursUnit || '').toString().trim(),
    );
    if (!completeRow) {
      e.coverageRows =
        'Add at least one coverage row with Plant, Hourly Rate, Hours, and Unit.';
    }
    // Terms
    req('PaymentTerms', 'Payment terms are required.');
    if (
      model.CancellationNoticeDays === '' ||
      model.CancellationNoticeDays == null
    ) {
      e.CancellationNoticeDays = 'Cancellation notice is required.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = (ev) => {
    ev.preventDefault();
    if (!validate()) {
      scrollToFirstError();
      return;
    }

    // Drop blank bullets / rows so they don't leave ghost entries in the
    // PDF. Coerce numeric fields to Number (or null for blank) so the
    // SharePoint currency / number columns accept them. Compose the PDF
    // coverage text from the structured hours + unit when the row doesn't
    // already carry free text (legacy rows keep their original wording).
    const supportBullets = (model.supportBullets || [])
      .map((b) => (b || '').trim())
      .filter(Boolean);
    const coverageRows = (model.coverageRows || [])
      .map((r) => {
        const hours =
          r.hoursAllocated === '' || r.hoursAllocated == null
            ? null
            : Number(r.hoursAllocated);
        const unit = (r.hoursUnit || '').trim();
        const composed = [hours != null ? `${hours} hrs` : '', unit]
          .filter(Boolean)
          .join(' ');
        return {
          plant: (r.plant || '').trim(),
          plantCode: String(r.plantCode || '').toUpperCase().trim(),
          hourlyRateUsd:
            r.hourlyRateUsd === '' || r.hourlyRateUsd == null
              ? null
              : Number(r.hourlyRateUsd),
          hoursAllocated: hours,
          hoursUnit: unit,
          coverage: (r.coverage || '').trim() || composed,
        };
      })
      .filter(
        (r) =>
          r.plantCode ||
          r.plant ||
          r.hourlyRateUsd != null ||
          r.hoursAllocated != null ||
          r.coverage,
      );

    const cancellationDays =
      model.CancellationNoticeDays === '' ||
      model.CancellationNoticeDays == null
        ? null
        : Number(model.CancellationNoticeDays);

    onSave({
      ...model,
      SupplierCode: model.SupplierCode || '',
      supportBullets,
      coverageRows,
      CancellationNoticeDays: cancellationDays,
    });
  };

  return (
    <form onSubmit={submit} noValidate>
      {/* ---------- Proposal ---------- */}
      <Section title="Proposal">
        <div className="form-grid">
          <TextField
            label="Proposal Date *"
            type="date"
            value={model.ProposalDate}
            onChange={(v) => set('ProposalDate', v)}
            error={errors.ProposalDate}
          />
          <TextField
            label="Proposal Title *"
            value={model.ProposalTitle}
            onChange={(v) => set('ProposalTitle', v)}
            error={errors.ProposalTitle}
            full
          />
        </div>
      </Section>

      {/* ---------- Prepared For (Customer) ---------- */}
      <Section title="Prepared For">
        <div className="form-grid">
          {/* Supplier picker — link this proposal to a customer (a.k.a.
              supplier internally). Choosing an existing one prefills the
              fields below; "New customer" creates the supplier on send. */}
          <SelectField
            label="Customer *"
            value={supplierChoice}
            onChange={onSupplierChoice}
            options={supplierOptions}
            placeholder="— select customer —"
            error={errors.SupplierCode}
            full
          />
          <TextField
            label="Contact Name *"
            value={model.CustomerContactName}
            onChange={(v) => set('CustomerContactName', v)}
            error={errors.CustomerContactName}
          />
          <TextField
            label="Company *"
            value={model.CustomerCompany}
            onChange={(v) => set('CustomerCompany', v)}
            error={errors.CustomerCompany}
          />
          <TextField
            label="Street *"
            value={model.CustomerStreet}
            onChange={(v) => set('CustomerStreet', v)}
            error={errors.CustomerStreet}
            full
          />
          <TextField
            label="City *"
            value={model.CustomerCity}
            onChange={(v) => set('CustomerCity', v)}
            error={errors.CustomerCity}
          />
          <TextField
            label="State / Province *"
            value={model.CustomerState}
            onChange={(v) => set('CustomerState', v)}
            error={errors.CustomerState}
          />
          <TextField
            label="Zip / Postal Code *"
            value={model.CustomerZip}
            onChange={(v) => set('CustomerZip', v)}
            error={errors.CustomerZip}
          />
          <PhoneField
            label="Phone *"
            value={model.CustomerPhone}
            onChange={(v) => set('CustomerPhone', v)}
            error={errors.CustomerPhone}
          />
        </div>
      </Section>

      {/* ---------- Prepared By (Proxemics rep) ---------- */}
      <Section title="Prepared By">
        <div className="form-grid">
          <TextField
            label="Name *"
            value={model.PreparedByName}
            onChange={(v) => set('PreparedByName', v)}
            error={errors.PreparedByName}
          />
          <PhoneField
            label="Phone *"
            value={model.PreparedByPhone}
            onChange={(v) => set('PreparedByPhone', v)}
            error={errors.PreparedByPhone}
          />
          <TextField
            label="Email *"
            value={model.PreparedByEmail}
            onChange={(v) => set('PreparedByEmail', v)}
            error={errors.PreparedByEmail}
            full
          />
        </div>
      </Section>

      {/* ---------- Content (intro + support bullets) ---------- */}
      <Section title="Overview">
        <TextArea
          label="Intro Paragraph *"
          rows={4}
          value={model.Intro}
          onChange={(v) => set('Intro', v)}
          error={errors.Intro}
        />
      </Section>

      <Section title="On-Site Liaison Support *">
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: 8,
          }}
        >
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={addBullet}
          >
            + Add Bullet
          </button>
        </div>
        {errors.supportBullets && (
          <div className="error" style={{ marginBottom: 8 }}>
            {errors.supportBullets}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(model.supportBullets || []).map((b, i) => (
            <div key={i} style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={b}
                onChange={(e) => setBullet(i, e.target.value)}
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => removeBullet(i)}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </Section>

      {/* ---------- Coverage & Rate ---------- */}
      <Section title="Coverage & Rate *">
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: 8,
          }}
        >
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={addRow}
          >
            + Add Row
          </button>
        </div>
        {errors.coverageRows && (
          <div
            className="error"
            style={{ marginBottom: 8 }}
          >
            {errors.coverageRows}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {(model.coverageRows || []).map((r, i) => {
            // Backward-compat: a legacy row may have a free-text plant
            // label but no code. Surface that label as a hint so the
            // Director knows which plant to re-pick from the dropdown.
            const legacyPlantHint =
              !r.plantCode && r.plant ? r.plant : '';
            return (
              <div
                key={i}
                style={{
                  border: '1px solid var(--brand-border)',
                  borderRadius: 6,
                  padding: 12,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'var(--brand-muted)',
                    }}
                  >
                    Plant {i + 1}
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => removeRow(i)}
                  >
                    Remove
                  </button>
                </div>
                <div className="form-grid">
                  <SelectField
                    label="Plant *"
                    value={(r.plantCode || '').toUpperCase()}
                    onChange={(v) => onRowPlant(i, v)}
                    options={plantOptions}
                    placeholder="— select plant —"
                    full
                  />
                  {legacyPlantHint && (
                    <p
                      className="muted tiny"
                      style={{ margin: '-4px 0 4px', gridColumn: '1 / -1' }}
                    >
                      Previously entered: “{legacyPlantHint}” — re-pick the
                      plant above to link it.
                    </p>
                  )}
                  <TextField
                    label="Hourly Rate (USD) *"
                    type="number"
                    step="0.01"
                    min="0"
                    value={r.hourlyRateUsd}
                    onChange={(v) => setRow(i, { hourlyRateUsd: v })}
                  />
                  <TextField
                    label="Hours of Coverage *"
                    type="number"
                    step="0.25"
                    min="0"
                    value={r.hoursAllocated}
                    onChange={(v) => setRow(i, { hoursAllocated: v })}
                    placeholder="e.g. 8"
                  />
                  <SelectField
                    label="Coverage Unit *"
                    value={r.hoursUnit || ''}
                    onChange={(v) => setRow(i, { hoursUnit: v })}
                    options={HOURS_UNIT_OPTIONS}
                    placeholder="— select unit —"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* ---------- Terms ---------- */}
      <Section title="Terms & Conditions">
        <div className="form-grid">
          <SelectField
            label="Payment Terms *"
            value={paymentTermsChoice}
            onChange={onPaymentTermsChoice}
            options={PAYMENT_TERMS_OPTIONS}
            error={
              paymentTermsChoice !== 'Other' ? errors.PaymentTerms : undefined
            }
          />
          {paymentTermsChoice === 'Other' && (
            <TextField
              label="Payment Terms (Other) *"
              value={model.PaymentTerms}
              onChange={(v) => set('PaymentTerms', v)}
              placeholder="e.g. Net 45, Due on receipt"
              error={errors.PaymentTerms}
            />
          )}
          <TextField
            label="Cancellation Notice (days) *"
            type="number"
            min="0"
            value={model.CancellationNoticeDays}
            onChange={(v) => set('CancellationNoticeDays', v)}
            error={errors.CancellationNoticeDays}
          />
          <TextArea
            label="Additional Terms"
            rows={3}
            value={model.AdditionalTerms}
            onChange={(v) => set('AdditionalTerms', v)}
          />
        </div>
      </Section>

      {/* Footer buttons:
          - Left column: "* Required fields" hint, with the Delete button
            stacked beneath it in edit mode.
          - Right column: [Cancel] + [Save / Update].
          The delete button is styled with the danger outline so it reads
          as a destructive action without shouting. */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          marginTop: 24,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 8,
          }}
        >
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            * Required fields
          </p>
          {isEditing && onDelete && (
            <button
              type="button"
              className="btn btn-outline"
              onClick={onDelete}
              disabled={saving || deleting}
              style={{
                color: 'var(--brand-danger)',
                borderColor: 'var(--brand-danger)',
              }}
            >
              {deleting ? 'Deleting…' : 'Delete Proposal'}
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="btn btn-outline"
            onClick={onCancel}
            disabled={saving || deleting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving || deleting}
          >
            {saving
              ? isEditing
                ? 'Updating…'
                : 'Saving…'
              : isEditing
                ? 'Update Proposal'
                : 'Save Proposal'}
          </button>
        </div>
      </div>
    </form>
  );
}
